import { useEffect, useState } from "react";
import { api, resolvePhoto } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DateFieldFr } from "@/components/DateFieldFr";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CascadeSelect } from "@/components/CascadeSelect";
import { toast } from "sonner";
import { Loader2, Upload, X, Camera, Check, AlertTriangle } from "lucide-react";
import { categoryIcon } from "@/lib/categoryIcons";

const STATES = ["vivant", "abattu", "pieces", "pret-a-cuire"];

const empty = {
  category: "poulet-de-chair", state: "vivant", quantity: 10, avg_weight: "1,8 kg",
  price: 4500, price_unit: "par tête", region: "", city: "",
  availability_date: new Date().toISOString().slice(0, 10),
  delivery_available: false, photos: [], comment: "", neighborhood: "",
};

export const ListingForm = ({ open, onOpenChange, initial, categories, onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...empty, ...initial } : empty);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEggs = form.category === "oeufs";

  const selectCategory = (slug) => {
    if (slug === "oeufs") {
      setForm((f) => ({ ...f, category: "oeufs", state: "na", price_unit: "par plateau", avg_weight: "" }));
    } else {
      setForm((f) => ({ ...f, category: slug, state: f.state === "na" ? "vivant" : f.state, price_unit: f.price_unit === "par plateau" ? "par tête" : f.price_unit }));
    }
  };

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      }
    } catch (e) {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const submit = async () => {
    if (!form.region || !form.neighborhood) { toast.error(t("common.required")); return; }
    setSaving(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), price: Number(form.price) };
      if (initial?.id) {
        await api.put(`/listings/${initial.id}`, payload);
        toast.success(t("listingForm.updatedRevalidate"));
      } else {
        await api.post("/listings", payload);
        toast.success(t("listingForm.published"));
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="listing-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-secondary">
            {initial?.id ? t("listingForm.editTitle") : t("listingForm.title")}
          </DialogTitle>
          <DialogDescription>{t("listingForm.chooseCategory")}</DialogDescription>
        </DialogHeader>

        {initial?.id && (
          <div className="rounded-xl bg-accent/40 border border-border p-3 flex items-start gap-2 text-sm text-foreground" data-testid="revalidate-notice">
            <AlertTriangle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>{t("listingForm.revalidateNote")}</span>
          </div>
        )}

        {/* Category icon grid */}
        <div>
          <Label className="text-base font-bold text-secondary">{t("listingForm.chooseCategory")}</Label>
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
            {categories.map((c) => {
              const active = form.category === c.slug;
              return (
                <button key={c.slug} type="button" onClick={() => selectCategory(c.slug)} data-testid={`cat-icon-${c.slug}`}
                  className={`min-h-[84px] rounded-2xl border-2 p-2 flex flex-col items-center justify-center gap-1.5 transition-colors active:scale-95 ${
                    active ? "border-secondary bg-secondary/10 ring-2 ring-secondary/20" : "border-border bg-card hover:border-secondary/50"
                  }`}>
                  <img src={categoryIcon(c.slug)} alt={t(`categories.${c.slug}`)} className="h-9 w-9 object-contain" loading="lazy" />
                  <span className="text-[11px] font-semibold text-secondary text-center leading-tight">{t(`categories.${c.slug}`)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 py-1">
          {!isEggs && (
            <div>
              <Label>{t("listingForm.state")}</Label>
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="form-state"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{t(`states.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>{isEggs ? t("listingForm.plateaux") : t("listingForm.quantity")}</Label>
            <Input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1.5 h-12" data-testid="form-quantity" />
          </div>
          {!isEggs && (
            <div>
              <Label>{t("listingForm.avgWeight")}</Label>
              <Input value={form.avg_weight} onChange={(e) => set("avg_weight", e.target.value)} className="mt-1.5 h-12" data-testid="form-weight" />
            </div>
          )}
          <div>
            <Label>{t("listingForm.price")}</Label>
            <Input type="number" min={0} value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5 h-12" data-testid="form-price" />
          </div>
          <div>
            <Label>{t("listingForm.priceUnit")}</Label>
            {isEggs ? (
              <Input value={t("listingForm.perPlateau")} disabled className="mt-1.5 h-12" data-testid="form-price-unit" />
            ) : (
              <Select value={form.price_unit} onValueChange={(v) => set("price_unit", v)}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="form-price-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["par tête", "par kg", "par lot"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label>{t("listingForm.availabilityDate")}</Label>
            <div className="mt-1.5"><DateFieldFr value={form.availability_date} onChange={(v) => set("availability_date", v)} testid="form-date" className="h-12" /></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border px-3 h-12 sm:mt-6">
            <Label className="cursor-pointer">{t("listingForm.delivery")}</Label>
            <Switch checked={form.delivery_available} onCheckedChange={(v) => set("delivery_available", v)} data-testid="form-delivery" />
          </div>
        </div>

        {/* Mali cascade */}
        <CascadeSelect region={form.region} city={form.city} neighborhood={form.neighborhood} onRegion={(v) => set("region", v)} onCity={(v) => set("city", v)} onNeighborhood={(v) => set("neighborhood", v)} large={false} testid="form-cascade" />

        <div>
          <Label>{t("listingForm.comment")}</Label>
          <Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)}
            placeholder={t("listingForm.commentPlaceholder")} className="mt-1.5" rows={2} data-testid="form-comment" />
        </div>

        {/* Photos: gallery + live camera */}
        <div>
          <Label>{t("listingForm.photos")}</Label>
          <div className="mt-2 flex flex-wrap gap-3 items-center">
            {form.photos.map((p) => (
              <div key={p} className="relative h-20 w-20 rounded-xl overflow-hidden border border-border">
                <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => removePhoto(p)} type="button"
                  className="absolute top-0.5 right-0.5 bg-red-600 rounded-full p-1 text-white" data-testid="remove-photo">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <label className="h-20 w-24 rounded-xl border-2 border-dashed border-secondary/50 bg-secondary/5 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-secondary transition-colors" data-testid="take-photo-label">
              {uploading ? <Loader2 className="h-6 w-6 animate-spin text-secondary" /> : <Camera className="h-7 w-7 text-secondary" />}
              <span className="text-[10px] font-semibold text-secondary">{t("listingForm.takePhoto")}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => e.target.files && uploadPhotos(e.target.files)} data-testid="camera-input" />
            </label>
            <label className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary transition-colors" data-testid="add-photos-label">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <input type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => e.target.files && uploadPhotos(e.target.files)} data-testid="photo-input" />
            </label>
          </div>
        </div>

        <DialogFooter className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-14 rounded-2xl border-2 border-destructive text-destructive hover:bg-destructive/5 text-base font-bold" data-testid="form-cancel">
            <X className="h-6 w-6 mr-1.5" strokeWidth={2.5} /> {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving} className="h-14 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-base font-bold" data-testid="form-submit">
            {saving ? <Loader2 className="h-6 w-6 mr-1.5 animate-spin" /> : <Check className="h-6 w-6 mr-1.5" strokeWidth={2.5} />}
            {t("common.validate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
